import React, { useEffect, useRef, useState } from 'react';
import { useNavigation } from '../context/NavigationContext';

interface InstallerProps {
  text?: string;
  className?: string;
  /** Which edge of the button the dropdown hangs from. Use "left" when the button sits near the
   * left edge of its container (e.g. the footer) so the dropdown opens rightward instead of
   * overflowing off-screen. Defaults to "right" (dropdown's right edge aligns with the button's),
   * which fits the nav's right-aligned placement. */
  align?: 'left' | 'right';
}

const CHROME_STORE_URL = 'https://chromewebstore.google.com/detail/stashr/fkhhemhabdhaobakcooochcbpclmlifc';

// Rough rendered height of the two-item dropdown, plus a margin of safety — used to decide
// whether there's room to open downward before the button's own position is known.
const ESTIMATED_DROPDOWN_HEIGHT = 210;

const Installer: React.FC<InstallerProps> = ({ text, className, align = 'right' }) => {
  const { navigate } = useNavigation();
  const [open, setOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleToggle = () => {
    if (!open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      // Only flip upward if there's actually more room that way — a button near the very top of
      // a short viewport shouldn't flip into negative space either.
      setOpenUpward(spaceBelow < ESTIMATED_DROPDOWN_HEIGHT && rect.top > spaceBelow);
    }
    setOpen((prev) => !prev);
  };

  const handleAiClick = () => {
    setOpen(false);
    navigate('/mcp');
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={handleToggle}
        className={`primary-button white-color ${className || ''}`}
        style={{ border: 'none', cursor: 'pointer' }}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {text}
      </button>

      {open && (
        <div
          className={`installer-dropdown installer-dropdown-${align} ${openUpward ? 'installer-dropdown-up' : ''}`}
          role="menu"
        >
          <a
            href={CHROME_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            role="menuitem"
            className="installer-dropdown-item"
            onClick={() => setOpen(false)}
          >
            <span className="installer-dropdown-title">Chrome extension</span>
            <span className="installer-dropdown-subtitle">Recommends a card automatically at checkout</span>
          </a>
          <button
            role="menuitem"
            className="installer-dropdown-item"
            onClick={handleAiClick}
          >
            <span className="installer-dropdown-title">Claude, ChatGPT &amp; Gemini</span>
            <span className="installer-dropdown-subtitle">Ask your AI assistant directly, no install</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default Installer;
